import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays, Store, PhoneCall, Mail, ClipboardList, LayoutDashboard,
  Settings, LogOut, Menu, X, Upload, ChevronDown, PanelLeftClose, PanelLeftOpen,
  Target, Trophy, Phone, Handshake, DoorOpen, Route, Building2, ShieldAlert, ClipboardCheck, LineChart, Clock, Users, MailCheck, Copy, GraduationCap, Sparkles, Wallet, Megaphone, Compass, BarChart3
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "cdp", "chef"] },
  { to: "/planning", label: "Planning", icon: CalendarDays, roles: ["admin", "cdp", "chef", "lecture_seule", "prospecteur", "superviseur"] },
  { to: "/planning-ideal", label: "Planning Idéal", icon: Sparkles, roles: ["admin", "cdp", "superviseur"] },
  { to: "/planning-pap", label: "Planning porte-à-porte", icon: DoorOpen, roles: ["admin", "pap", "terrain", "lecture_seule"], board: "board_pap" },
  { to: "/planning-ideal-pap", label: "Idéal porte-à-porte", icon: Compass, roles: ["admin", "pap"] },
  { to: "/planning-rue-itinerance", label: "Planning rue — itinérance", icon: Route, roles: ["admin", "terrain", "lecture_seule"], board: "board_rue_itinerance" },
  { to: "/planning-rue-fixe", label: "Planning rue — fixe", icon: Building2, roles: ["admin", "terrain", "lecture_seule"], board: "board_rue_fixe" },
  { to: "/suivi-coordination", label: "Suivi coordination", icon: ClipboardCheck, roles: ["admin", "lecture_seule"], viewerPerm: "declare_coordination" },
  { to: "/annonces", label: "Annonces", icon: Megaphone, roles: ["admin", "lecture_seule"], viewerPerm: "broadcast" },
  { to: "/equipes", label: "Équipes", icon: Users, roles: ["admin", "lecture_seule"] },
  { to: "/terrain-jour", label: "Terrain du jour", icon: PhoneCall, roles: ["lecture_seule"] },
  { to: "/spots", label: "Banque de spots", icon: Store, roles: ["admin", "cdp", "prospecteur", "superviseur"] },
  { to: "/doublons-spots", label: "Doublons de spots", icon: Copy, roles: ["admin", "cdp", "superviseur"] },
  { to: "/prospection", label: "Prospection", icon: PhoneCall, roles: ["admin", "cdp", "superviseur"] },
  { to: "/appels", label: "Appels", icon: Phone, roles: ["admin", "cdp", "prospecteur", "superviseur"] },
  { to: "/formation", label: "Formation", icon: GraduationCap, roles: ["admin", "cdp", "superviseur"] },
  { to: "/objectifs", label: "Mes Objectifs", icon: Target, roles: ["cdp"] },
  { to: "/suivi-cdp", label: "Suivi CDP", icon: Trophy, roles: ["admin", "superviseur"] },
  { to: "/primes-rebooking", label: "Primes rebooking", icon: Wallet, roles: ["admin"] },
  { to: "/tendances", label: "Tendances", icon: LineChart, roles: ["admin", "superviseur"] },
  { to: "/kpis-equipes", label: "KPIs équipes", icon: BarChart3, roles: ["admin"] },
  { to: "/pointage", label: "Pointage & Heures", icon: Clock, roles: ["admin"] },
  { to: "/exceptions", label: "Journal des exceptions", icon: ShieldAlert, roles: ["admin", "superviseur"], badgeKey: "exceptions_pending" },
  { to: "/emails", label: "Emails", icon: Mail, roles: ["admin", "cdp", "prospecteur", "superviseur"] },
  { to: "/negociations", label: "Négociations", icon: Handshake, roles: ["admin", "cdp", "prospecteur", "superviseur"], badgeKey: "actionable" },
  { to: "/confirmations-magasins", label: "Confirmations magasins", icon: MailCheck, roles: ["admin", "superviseur"] },
  { to: "/bulletins", label: "Bulletins", icon: ClipboardList, roles: ["admin", "cdp", "chef"], feature: "bulletins" },
  { to: "/import", label: "Import BDD", icon: Upload, roles: ["admin"] },
  { to: "/admin", label: "Administration", icon: Settings, roles: ["admin"], badgeKey: "mandat_alerts" },
];

const ROLE_LABEL = { admin: "Administrateur", cdp: "CDP", chef: "Chef d'équipe", lecture_seule: "Lecture seule", pap: "Porte-à-porte", terrain: "Responsable planning terrain", prospecteur: "Prospecteur terrain", superviseur: "Superviseur" };

function NavItem({ item, onClick, collapsed, badge = 0 }) {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex items-center rounded-xl py-2.5 text-sm font-semibold transition-colors ${
          collapsed ? "justify-center px-0" : "gap-3 px-3"
        } ${
          isActive
            ? "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"
            : "text-[rgba(24,24,24,0.66)] hover:bg-[rgba(238,114,46,0.08)] hover:text-[var(--shariti-ink)]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className="relative shrink-0">
            <Icon className={`h-[18px] w-[18px] ${isActive ? "text-[var(--shariti-orange)]" : ""}`} />
            {collapsed && badge > 0 && (
              <span className="absolute -right-1.5 -top-1.5 grid h-3.5 min-w-[14px] place-items-center rounded-full bg-[#D64545] px-0.5 text-[9px] font-bold text-white"
                data-testid={`nav-badge-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {badge > 9 ? "9+" : badge}
              </span>
            )}
          </span>
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && badge > 0 && (
            <span className="ml-auto grid h-5 min-w-[20px] place-items-center rounded-full bg-[#D64545] px-1.5 text-[11px] font-bold text-white"
              data-testid={`nav-badge-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="bg-[var(--shariti-ink)] font-semibold">
          {item.label}{badge > 0 ? ` · ${badge} en attente` : ""}
        </TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

function LogoMark() {
  return <img src="/shariti-logo.webp" alt="Shariti" className="h-9 w-9 object-contain" />;
}

function Logo({ collapsed }) {
  if (collapsed) return <LogoMark />;
  return (
    <div className="flex items-center gap-2.5 px-2">
      <img src="/shariti-logo.webp" alt="Shariti" className="h-10 w-10 object-contain" />
      <div className="leading-none">
        <div className="text-[17px] font-extrabold tracking-tight text-[var(--shariti-orange)]">SHARITI</div>
        <div className="text-[9px] font-light tracking-[0.25em] text-muted-ink">FACE TO FACE</div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "1" ||
      window.location.pathname.startsWith("/planning")
  );
  // Survol : quand le menu est replié (épinglé fermé), le passage du curseur le
  // déploie en superposition, et il se replie dès que le curseur en sort.
  const [hovering, setHovering] = useState(false);
  // Preference the user set manually (restored when leaving the planning page).
  const userPrefRef = useRef(localStorage.getItem("sidebar-collapsed") === "1");
  const prevPathRef = useRef(location.pathname);

  // Auto-collapse when opening the planning, restore preference when leaving it.
  useEffect(() => {
    const isPlanning = location.pathname.startsWith("/planning");
    const wasPlanning = prevPathRef.current.startsWith("/planning");
    if (isPlanning && !wasPlanning) setCollapsed(true);
    else if (!isPlanning && wasPlanning) setCollapsed(userPrefRef.current);
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      userPrefRef.current = next;
      localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
      return next;
    });
  };

  const items = NAV.filter((n) => {
    if (user && !n.roles.includes(user.role)) return false;
    // Tableaux terrain : n'afficher que ceux cochés pour un « Responsable planning
    // terrain » ou un profil « Lecture seule ».
    if (n.board && ["terrain", "lecture_seule"].includes(user?.role) && !(user.permissions || {})[n.board]) return false;
    // Éléments réservés à une permission granulaire « lecture seule » (ex : chargée des opérations).
    if (n.viewerPerm && user?.role === "lecture_seule" && !(user.permissions || {})[n.viewerPerm]) return false;
    // Modules MASQUÉS par l'admin pour cet utilisateur (ex : « bulletins »).
    if (n.feature && (user?.hidden_features || []).includes(n.feature)) return false;
    return true;
  });
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  // Compteur « actions en attente » (contre-propositions à traiter) pour la pastille.
  // Rafraîchi : au changement de page, toutes les 60s, et sur événement « acks-updated »
  // (émis après une approbation / reproposition dans Négociations ou Emails).
  const [summary, setSummary] = useState({});
  useEffect(() => {
    if (!user || !["admin", "cdp", "prospecteur"].includes(user.role)) return;
    let alive = true;
    const refetch = async () => {
      const [ack, mandats, exc] = await Promise.all([
        api.get("/ack/summary").catch(() => ({ data: {} })),
        user.role === "admin" ? api.get("/mandats/alerts").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        user.role === "admin" ? api.get("/planning/exceptions-pending-count").catch(() => ({ data: { pending: 0 } })) : Promise.resolve({ data: { pending: 0 } }),
      ]);
      if (alive) setSummary({ ...(ack.data || {}), mandat_alerts: (mandats.data || []).length, exceptions_pending: (exc.data || {}).pending || 0 });
    };
    refetch();
    const onEvt = () => refetch();
    window.addEventListener("shariti:acks-updated", onEvt);
    const poll = setInterval(refetch, 60000);
    return () => { alive = false; window.removeEventListener("shariti:acks-updated", onEvt); clearInterval(poll); };
  }, [user, location.pathname]);
  const badgeFor = (it) => (it.badgeKey ? (summary[it.badgeKey] || 0) : 0);

  // État VISUEL effectif : replié si épinglé fermé ET non survolé.
  // Le survol ne change PAS le décalage du contenu (`main`), qui reste basé sur l'état
  // épinglé → le menu se déploie en superposition sans faire sauter la mise en page.
  const effectiveCollapsed = collapsed && !hovering;
  const floating = collapsed && hovering;   // déployé par survol (overlay)

  return (
    <TooltipProvider>
      <div className="flex min-h-screen bg-[var(--bg-subtle)]">
        {/* Desktop sidebar */}
        <aside
          data-testid="app-sidebar"
          data-collapsed={effectiveCollapsed ? "true" : "false"}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          className={`fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-[var(--border-soft)] bg-white transition-[width] duration-200 lg:flex ${effectiveCollapsed ? "w-[76px]" : "w-64"} ${floating ? "shadow-2xl" : ""}`}
        >
          <div className={`flex h-16 items-center border-b border-[var(--border-soft)] ${effectiveCollapsed ? "justify-center px-2" : "gap-1 px-4"}`}>
            <button onClick={() => navigate("/")} className={effectiveCollapsed ? "grid place-items-center" : "min-w-0 flex-1 text-left"} data-testid="logo-home">
              <Logo collapsed={effectiveCollapsed} />
            </button>
            {!collapsed && (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <button onClick={toggleCollapse} data-testid="sidebar-collapse-toggle-top" aria-label="Réduire le menu"
                    className="ml-auto shrink-0 rounded-lg p-1.5 text-[rgba(24,24,24,0.55)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-ink)]">
                    <PanelLeftClose className="h-[18px] w-[18px]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-[var(--shariti-ink)] font-semibold">Réduire le menu</TooltipContent>
              </Tooltip>
            )}
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden p-3">
            {items.map((it) => <NavItem key={it.to} item={it} collapsed={effectiveCollapsed} badge={badgeFor(it)} />)}
          </nav>
          <div className="space-y-2 border-t border-[var(--border-soft)] p-3">
            <CollapseToggle narrow={effectiveCollapsed} pinned={collapsed} onToggle={toggleCollapse} />
            <UserMenu user={user} logout={logout} initials={initials} navigate={navigate} collapsed={effectiveCollapsed} />
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-[var(--border-soft)] bg-white px-4 lg:hidden">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="rounded-lg p-2 hover:bg-[var(--surface-2)]">
            <Menu className="h-5 w-5" />
          </button>
          <Logo />
          <div className="h-9 w-9 rounded-full bg-[var(--shariti-orange)] text-white grid place-items-center text-xs font-bold">{initials}</div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />
              <motion.aside initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
                transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.28 }}
                className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white lg:hidden">
                <div className="flex h-16 items-center justify-between border-b border-[var(--border-soft)] px-4">
                  <Logo />
                  <button onClick={() => setMobileOpen(false)} className="rounded-lg p-2 hover:bg-[var(--surface-2)]"><X className="h-5 w-5" /></button>
                </div>
                <nav className="flex-1 space-y-1 overflow-y-auto p-3">
                  {items.map((it) => <NavItem key={it.to} item={it} onClick={() => setMobileOpen(false)} badge={badgeFor(it)} />)}
                </nav>
                <div className="border-t border-[var(--border-soft)] p-3">
                  <UserMenu user={user} logout={logout} initials={initials} navigate={navigate} />
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Main */}
        <main className={`min-w-0 flex-1 transition-[padding] duration-200 ${collapsed ? "lg:pl-[76px]" : "lg:pl-64"}`}>
          <div className="@container/page pt-14 lg:pt-0">{children}</div>
        </main>
      </div>
    </TooltipProvider>
  );
}

function CollapseToggle({ narrow, pinned, onToggle }) {
  // narrow  : affichage étroit (icône seule) — menu replié et non survolé
  // pinned  : état ÉPINGLÉ fermé (base). Détermine l'action/libellé quand le menu est large :
  //   - pinned (déployé par survol) → cliquer ÉPINGLE ouvert ("Garder le menu ouvert")
  //   - non épinglé (déjà ouvert)   → cliquer réduit ("Réduire le menu")
  const btn = (
    <button
      onClick={onToggle}
      data-testid="sidebar-collapse-toggle"
      className={`flex items-center rounded-xl py-2 text-xs font-semibold text-[rgba(24,24,24,0.6)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-ink)] ${narrow ? "w-full justify-center px-0" : "w-full gap-2 px-3"}`}
    >
      {narrow
        ? <PanelLeftOpen className="h-[18px] w-[18px]" />
        : (pinned
            ? <><PanelLeftOpen className="h-[18px] w-[18px]" /> Garder le menu ouvert</>
            : <><PanelLeftClose className="h-[18px] w-[18px]" /> Réduire le menu</>)}
    </button>
  );
  if (narrow) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent side="right" className="bg-[var(--shariti-ink)] font-semibold">Survolez pour déployer · cliquez pour épingler</TooltipContent>
      </Tooltip>
    );
  }
  return btn;
}

function UserMenu({ user, logout, initials, navigate, collapsed }) {
  const avatar = (
    <div className="h-9 w-9 shrink-0 rounded-full text-white grid place-items-center text-xs font-bold"
      style={{ background: user?.color && user?.role === "cdp" ? user.color : "var(--shariti-orange)", color: user?.role === "cdp" ? "#181818" : "#fff" }}>
      {initials}
    </div>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-btn" className={`flex w-full items-center rounded-xl p-2 text-left hover:bg-[var(--surface-2)] ${collapsed ? "justify-center" : "gap-3"}`}>
          {avatar}
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold">{user?.name}</div>
                <div className="truncate text-[11px] text-muted-ink">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-ink" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="menu-change-password">Changer mon mot de passe</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-[#D64545] focus:text-[#D64545]">
          <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PageHeader({ title, subtitle, action, children }) {
  return (
    <div className="shariti-gradient-header">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-3 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-1 text-sm font-light text-[rgba(24,24,24,0.7)]">{subtitle}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

export function PageBody({ children }) {
  return <div className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8">{children}</div>;
}
